import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { EnumSelectFormField, InputFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, FieldTitle, Spinner } from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { SFMapBoxSearchBoxInputTheme } from '@repo/shared-client/mapbox'
import { createFileRoute } from '@tanstack/react-router'
import SFLocationSearchField from '@web/src/components/primatives/sf-location-search-field/sf-location-search-field'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import SFRichText from '@web/src/components/primatives/sf-rich-text/sf-rich-text'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { RelationshipEnum } from '@web/src/lib/types/enumTypes'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import ProfileEditHeader from 'src/features/profile/profile-edit-header/profile-edit-header'
import ProfileEditMenu from 'src/features/profile/profile-edit-menu/profile-edit-menu'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { UPDATE_PROFILE } from 'src/lib/mutations'
import type {
  FindProfileEditGeneralQuery,
  FindProfileEditGeneralQueryVariables,
  RelationshipEnum as RelationshipEnumGql,
} from 'types/graphql'

type ProfileGeneralFormValues = {
  relationship: string
  tagLine?: string | null
  about?: string | null
  location: {
    name?: string | null
    longitude?: number | null
    latitude?: number | null
  }
}

const PROFILE_EDIT_GENERAL_QUERY: TypedDocumentNode<FindProfileEditGeneralQuery, FindProfileEditGeneralQueryVariables> =
  gql`
    query FindProfileEditGeneralQuery($profileId: String!) {
      profile: profile(id: $profileId) {
        ...ProfileInfo
      }
    }
    ${ProfileInfo}
    ${ProfileMediaInfo}
  `

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId_/edit/general')({
  component: EditGeneralPage,
  head: () =>
    seo({
      title: `Edit General | ${env.VITE_APP_NAME}`,
      description: 'Profile Edit General Information',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, PROFILE_EDIT_GENERAL_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function EditGeneralPage() {
  'use no memo' // disable React Compiler to fix issues with react hook forms

  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<FindProfileEditGeneralQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [showSpinner, setShowSpinnerValue] = useState(false)

  const handleRefetch = () => {
    return apolloClient.refetchQueries({ include: [PROFILE_EDIT_GENERAL_QUERY] })
  }

  const formMethods = useForm<ProfileGeneralFormValues>({
    defaultValues: {
      relationship: data?.profile?.relationship ?? '',
      tagLine: data?.profile?.tagLine,
      about: data?.profile?.about,
      location: {
        name: data?.profile?.location,
        longitude: data?.profile?.locationLong,
        latitude: data?.profile?.locationLat,
      },
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    if (data?.profile) {
      formMethods.reset({
        relationship: data.profile.relationship ?? '',
        tagLine: data.profile.tagLine,
        about: data.profile.about,
        location: {
          name: data.profile.location,
          longitude: data.profile.locationLong,
          latitude: data.profile.locationLat,
        },
      })
    }
  }, [
    data?.profile?.location,
    data?.profile?.locationLong,
    data?.profile?.locationLat,
    data?.profile?.relationship,
    data?.profile?.tagLine,
    data?.profile?.about,
    formMethods,
  ])

  const [updateProfile] = useMutation(UPDATE_PROFILE, {
    onCompleted: () => {
      toast.success(t('Profile Updated'))
      handleRefetch()
      formMethods.reset()
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSubmit = (fieldData: ProfileGeneralFormValues) => {
    if (!data?.profile) return

    setShowSpinnerValue(true)
    updateProfile({
      variables: {
        id: data.profile.id,
        input: {
          relationship: fieldData.relationship ? (fieldData.relationship as RelationshipEnumGql) : null,
          about: fieldData.about,
          tagLine: fieldData.tagLine,
          location: fieldData.location.latitude ? fieldData.location.name : undefined,
          locationLat: fieldData.location.latitude ? fieldData.location.latitude : undefined,
          locationLong: fieldData.location.longitude ? fieldData.location.longitude : undefined,
        },
      },
    })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.profile) {
    return (
      <Card className="py-2">
        <CardContent>
          <p className="pt-3 text-destructive">{t('No Data')}</p>
        </CardContent>
      </Card>
    )
  }

  const profile = data.profile

  return (
    <div className="mx-auto w-full max-w-7xl px-0">
      <ProfileEditHeader profileId={profile.id} />
      <div className="mb-4 grid grid-cols-2 gap-3 px-2 md:grid-cols-4">
        <div className="col-span-2 mt-0 flex justify-center md:col-span-1 md:mt-6">
          <ProfileEditMenu className="hidden md:flex" profile={profile} w="80%" />
        </div>
        <div className="col-span-2 md:col-span-2">
          <Form<ProfileGeneralFormValues> className="w-full" formMethods={formMethods} onSubmit={onSubmit}>
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <EnumSelectFormField
                      enumType={RelationshipEnum}
                      label={t('Relationship Status')}
                      name="relationship"
                      placeholder={t('Select relationship status')}
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <InputFormField
                      inputProps={{ placeholder: t('Enter a catchy tag line for your profile') }}
                      label={t('Tag Line')}
                      name="tagLine"
                    />
                  </div>

                  <div className="col-span-2">
                    <FieldTitle className="mb-1.5">{t('About')}</FieldTitle>
                    <SFRichText
                      maxCharacters={10_000}
                      minHeight="10.8rem"
                      name="about"
                      placeholder={t('Tell people about yourself...')}
                    />
                  </div>

                  <div className="col-span-2">
                    <SFLocationSearchField
                      label={t('Your City')}
                      name="location"
                      rules={{
                        required: {
                          value: true,
                          message: t('You must select a valid location'),
                        },
                      }}
                      theme={SFMapBoxSearchBoxInputTheme}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="mt-6 flex w-full justify-end">
              <div className="flex items-center gap-2">
                {isDirty ? (
                  <Button onClick={() => formMethods.reset()} type="button" variant="outline">
                    {t('Cancel')}
                  </Button>
                ) : null}
                <Button disabled={!isDirty || showSpinner} type="submit">
                  {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
                  {t('Save')}
                </Button>
              </div>
            </div>
          </Form>
        </div>
        <div className="col-span-1 hidden md:block" />
      </div>
    </div>
  )
}
